import { IconButton, Portal } from '@mui/material';
import { ReactNode, UIEvent, useCallback, useEffect, useRef } from 'react';
import { useBoolean } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';

import { SourceType } from '@/types/document';

import { getDataTestId } from '@/utils/browser';

import { ImageViewer } from './ImageViewer';
import { PDFRenderer } from './PDFRenderer';

import {
  FileViewerAction,
  Lightbox,
  WebView,
  WebViewContainer,
} from './File.styled';

type FileViewerProps = {
  src: string;
  type: SourceType;
  onClose?: () => void;
  onPDFScrolledToBottom?: () => void;
};

const ESTIMATED_PADDING = 64;

export function FileViewer({
  src,
  type,
  onClose,
  onPDFScrolledToBottom,
}: FileViewerProps) {
  const fileContainerRef = useRef<HTMLElement>(null);
  const { value: isLoaded, setTrue: setLoaded } = useBoolean(false);

  const onScroll = useCallback(
    (e: UIEvent<HTMLElement> | HTMLElement) => {
      const target = e instanceof HTMLElement ? e : e.currentTarget;

      if (target && isLoaded) {
        const { scrollTop, scrollHeight, offsetHeight } = target;

        if (scrollTop >= scrollHeight - offsetHeight - ESTIMATED_PADDING) {
          onPDFScrolledToBottom?.();
        }
      }
    },
    [isLoaded, onPDFScrolledToBottom],
  );

  useEffect(() => {
    if (fileContainerRef.current) {
      onScroll(fileContainerRef.current);
    }
  }, [onScroll]);

  const render = useCallback(
    (children: ReactNode) => {
      return (
        <Portal>
          <Lightbox onScroll={onScroll} ref={fileContainerRef}>
            {children}
          </Lightbox>
          <FileViewerAction>
            <IconButton onClick={onClose}>
              <Icon name="close" />
            </IconButton>
          </FileViewerAction>
        </Portal>
      );
    },
    [onClose, onScroll],
  );

  if (type === SourceType.Web) {
    return render(
      <WebViewContainer {...getDataTestId('web-view')}>
        <WebView src={String(src)} />
      </WebViewContainer>,
    );
  }

  if (type === SourceType.Image) {
    return render(<ImageViewer url={src || ''} />);
  }

  if (type === SourceType.PDF) {
    return render(<PDFRenderer file={src} onLoaded={setLoaded} />);
  }

  return null;
}
